import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface PricePositionProps {
  /** Lowest observed price in the window. */
  low: number;
  /** Highest observed price in the window. */
  high: number;
  /** Current price to place between low and high. */
  current: number;
  /** Currency-ish formatter for the endpoints (defaults to $0.000). */
  format?: (n: number) => string;
}

const defaultFormat = (n: number) => `$${n.toFixed(3)}`;

/**
 * Shows where the current price sits within an observed [low, high] range —
 * far more decision-useful than a raw sparkline on a shopping page. Driven by
 * real min/max/current from price history; renders nothing if the range is
 * degenerate.
 */
export function PricePosition({ low, high, current, format = defaultFormat }: PricePositionProps) {
  const theme = useTheme();
  const t = theme.palette.tactical;

  if (!(high > low)) return null;
  const pos = Math.max(0.03, Math.min(0.97, (current - low) / (high - low)));

  const label =
    pos < 0.2
      ? '↓ AT RANGE LOW'
      : pos < 0.42
        ? 'BELOW AVG'
        : pos < 0.62
          ? 'NEAR AVG'
          : pos < 0.82
            ? 'ABOVE AVG'
            : '↑ NEAR HIGH';
  const color = pos < 0.25 ? t.green : pos < 0.5 ? t.accent : pos < 0.78 ? t.dim : t.red;

  return (
    <Box>
      <Box
        sx={{
          fontFamily: t.monoFont,
          // #115: the price-range values were 9px — too small to read. Bump to 12px.
          fontSize: 12,
          color: t.dim,
          letterSpacing: '0.05em',
          mb: 0.6,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>{format(low)}</span>
        <span style={{ color: t.dimmer }}>·</span>
        <span>{format(high)}</span>
      </Box>
      <Box sx={{ position: 'relative', height: 4, bgcolor: t.line, borderRadius: 1 }}>
        <Box
          sx={{
            position: 'absolute',
            top: -3,
            left: `${pos * 100}%`,
            transform: 'translateX(-50%)',
            width: 3,
            height: 10,
            bgcolor: color,
            borderRadius: 0.5,
            boxShadow: `0 0 6px ${color}aa`,
          }}
        />
      </Box>
      <Box sx={{ fontFamily: t.monoFont, fontSize: 11, color, letterSpacing: '0.08em', mt: 0.75 }}>
        {label}
      </Box>
    </Box>
  );
}

export default PricePosition;
